import { Component, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PaymentResponseDto } from '../shared/models/paymentResponseDto';
import { Currency } from '../shared/models/currency';
import { RequestsService } from '../shared/services/requests.service';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { CommonService } from '../shared/services/common.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Matter } from '../shared/models/matter';
import { debounceTime, firstValueFrom, switchMap } from 'rxjs';
import { MatterService } from '../shared/services/matter.service';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { SnackbarService } from '../shared/services/snackbar.service';
import { AttachmentData, RequestDocAttachmentResponse, RequestDocAttachmentDto } from '../shared/models/requestDocAttachment';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { UserRequestsService } from '../shared/services/user-requests.service';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserService } from '../shared/services/user.service';
import { UserRole } from '../shared/models/user-role';
import { AuditService } from '../shared/services/audit.service';

@Component({
    selector: 'xp-view-user-payment',
    templateUrl: './view-user-payment.component.html',
    styleUrls: ['./view-user-payment.component.scss']
})
export class ViewUserPaymentComponent implements OnInit {
    paymentRequestForm!: FormGroup;
    currentDate: Date = new Date(Date.now())
    paymentType: Boolean = false;
    isInternational: Boolean = false;
    showPropertyPurchaseCheckbox = false;
    showPurposeCodeDropdown = false;
    partnerList: any[] = [];
    filteredBankAccounts: any[] = [];
    displayFile: string[] = [];
    purposeCodeDropdown: any[] = [];
    currencyDropdown: Currency[] = [];
    bankAccounts: BankAccount[] = [];
    isLoading: Boolean = false;
    displayValue: string | null = '';
    vatDisplayValue: string | null = '';
    paymentMethodDropdown: string[] = ['CHAPS', 'Faster Payment'];
    matterDropdownData: Matter[] = [];
    noData: Boolean = false;
    matterNo: string = '';
    matterDesc: string = '';
    /** The clients on the request's matter. With more than one, the payee is ticked from them. */
    clientOptions: string[] = [];
    /** The clients ticked in the dropdown. Only the text is part of the form; it is these names joined. */
    readonly payeeClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payeeErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payee = this.paymentRequestForm?.get('name');
            return !!payee && payee.touched && payee.invalid;
        }
    };
    previousMatterNumber: string | null = null;
    fileNames: AttachmentData[] = [];
    typeOfPayment: string = '';
    clientOffice: string = '';
    showReturnReason: boolean = false;
    isDisabled = false;
    isUserPartner: boolean = false
    isRejectDisabled = false;
    isWithdrawDisabled = false;
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    actionstepBaseUrl: string = '';
    actionstepOverviewBaseUrl: string = '';
    actionstepMatterUrl: string = '';
    actionstepMatterOverviewUrl: string = '';
    selectedBankAccountId: string = '';
    showDateTransaction: WritableSignal<boolean> = signal(false);

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
        private requestService: RequestsService,
        private userRequestService: UserRequestsService,
        private bankAccountService: BankAccountService,
        private matterService: MatterService,
        private commonService: CommonService,
        private snackbarService: SnackbarService,
        private manageUserRoleService: ManageUserRoleService,
        private userService: UserService,
        private auditService: AuditService,
        private dialogRef: MatDialogRef<ViewUserPaymentComponent>,
        @Inject(MAT_DIALOG_DATA) public payment: PaymentResponseDto
    ) { }

    async ngOnInit() {
        this.initializeForm();
        this.setupAutocomplete();
        this.getPurchaseCodes();

        // Started first and not waited for, so the fields that depend on the user's role (such as the matter partner
        // question) do not wait behind the other loads.
        this.loadUserRole();

        [this.bankAccounts, this.actionstepBaseUrl, this.actionstepOverviewBaseUrl] = await Promise.all([
            this.bankAccountService.getAllBankAccounts(),
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl()
        ]);

        this.typeOfPayment = this.payment.paymentType ?? '';
        this.clientOffice = this.payment.clientOffice ?? '';

        this.filteredBankAccounts = this.bankAccounts;

        this.paymentRequestForm.get('clientBankAccount')!.valueChanges.subscribe((account: BankAccount | null) => {
            this.selectedBankAccountId = account?.accountId ?? '';
            if (this.matterNo) {
                this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            }
        });

        if (this.payment.clientBankAccountId) {
            this.paymentRequestForm.patchValue({
                clientBankAccount: this.resolveBankAccount(this.payment.clientBankAccountId) ?? null
            });
        }

        await this.filterBankAccounts();

        if (this.payment) {
            this.isInternational = this.payment.paymentType?.toLowerCase() === 'international';
            this.paymentType = this.payment.clientOffice?.toLowerCase() === 'office';

            this.patchFormValues(this.payment);
            this.updateValidators();
            this.showPropertyPurchaseCheckbox = !!this.payment.chapsRequired;
            this.showPurposeCodeDropdown = !!this.payment.isPropertyPurchase;
            this.toggleFormFieldsByStage();
            if (this.matterNo) {
                // Not waited for: it calls Actionstep, and only the Client Name and Payee fields depend on it.
                this.getClientDetails(Number(this.matterNo), false);
            }
        }

        this.setupFormSubscriptions();
    }

    private async loadUserRole(): Promise<void> {
        const role: UserRole = await this.manageUserRoleService.getUserPermissionsByRole(this.userService.userDetails?.userRole!);
        this.isUserPartner = role.partnerApprove;
    }

    initializeForm() {
        let vatAmountValidators = [Validators.pattern(/^[0-9.,]*$/)];

        if (this.paymentType) {
            vatAmountValidators.push(Validators.required);
        } else if (!this.paymentType) {
            vatAmountValidators = vatAmountValidators.filter(validator => validator !== Validators.required);
        }

        this.paymentRequestForm = this.fb.group({
            returnReason: [''],
            chapsRequired: [null, Validators.required],
            isPropertyPurchase: [false],
            purposeCode: [null],
            matter: [null, Validators.required],
            matterNumber: [null, Validators.required],
            clientName: ['', Validators.required],
            isMoniesReturned: [null, Validators.required],
            reason: ['', Validators.required],
            returnMoniesExplanationIfNo: [''],
            dateRequested: [null, Validators.required],
            requestedTime: [{ value: '' }],
            name: [null, Validators.required],
            amount: [null, Validators.required],
            vatAmount: [null],
            clientBankAccount: [null, Validators.required],
            currency: '',
            bankName: [''],
            intermBankName: [''],
            sortCode: [''],
            accountNo: [''],
            reference: ['', Validators.required],
            intermAccountNo: [''],
            intermSwift: [''],
            interReference: [''],
            finalBeneficiaryAccNo: [''],
            finalBeneficiarySwift: [''],
            finalReference: [''],
            otherInfo: [''],
            address: [''],
            isAccountDetailsVerifies: ['', Validators.required],
            verificationDate: [''],
            spokeTo: [''],
            verificationNoReason: [''],
            uploadedFiles: [[]],
            partnerAuthorising: [{ value: '', disabled: true }],
            matterPartner: [null],
            nonPartnerReason: [''],
            dateTransaction: '',
            transactionTime:''
        });
    }

    patchFormValues(payment: PaymentResponseDto) {
        this.paymentRequestForm.patchValue({
            returnReason: payment.returnReason,
            chapsRequired: payment.chapsRequired ? 'CHAPS' : 'Faster Payment',
            isPropertyPurchase: payment.isPropertyPurchase,
            purposeCode: payment.purchaseCode?.id,
            matter: payment.matter ? `${payment.matterNo} - ${payment.matter}` : null,
            matterNumber: payment.matterNo || null,
            isMoniesReturned: payment.moniesReturnedClient,
            reason: payment.reason,
            returnMoniesExplanationIfNo: this.payment.returnMoniesExplanationIfNo,
            dateRequested: payment.dateRequested ? new Date(payment.dateRequested) : null,
            clientName: payment.clientName,
            name: payment.payee,
            amount: payment.amount,
            vatAmount: payment.vatAmount,
            currency: payment.currencyId ?? null,
            bankName: payment.bankName,
            intermBankName: payment.intermediaryBankName,
            sortCode: payment.sortCode?.replace(/\D/g, ''),
            accountNo: payment.accountNumber,
            reference: payment.payeeReference,
            intermAccountNo: payment.intermediaryAccountNumber,
            intermSwift: payment.intermediarySwiftBicCode,
            interReference: payment.intermediaryReference,
            finalBeneficiaryAccNo: payment.beneficiaryAccountNumber,
            finalBeneficiarySwift: payment.swiftBicCode,
            finalReference: payment.reference,
            otherInfo: payment.otherInformation,
            address: payment.address ?? '',
            partnerAuthorising: payment.partnerAuthorisingName ?? '',
            isAccountDetailsVerifies: this.payment.accountTelephoneVerification === true ? 'Yes' : this.payment.accountTelephoneVerification === false ? 'No' : '',
            verificationDate: this.payment.accountTelephoneVerification === true && this.payment.telephoneVerificationDate? new Date(this.payment.telephoneVerificationDate): null,
            spokeTo: this.payment.accountTelephoneVerification === true ? this.payment.verificationSpokeTo : '',
            verificationNoReason: this.payment.accountTelephoneVerification === false ? this.payment.verificationNoReason : '',
            matterPartner: this.payment.matterPartner,
            nonPartnerReason: this.payment.nonPartnerReason,
            dateTransaction: this.payment.dateTransaction
        });

        if (this.payment.dateTransaction) {
            const d = new Date(this.payment.dateTransaction);
            this.paymentRequestForm.get('transactionTime')?.setValue(this.toTimeString(d));
            this.showDateTransaction.set(true);
        }
        else {
            this.showDateTransaction.set(false);
        }

        if (this.payment.dateRequested) {
            const d = new Date(this.payment.dateRequested);
            this.paymentRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        this.matterNo = this.payment?.matterNo?.toString() || '';

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;

            this.showLinkToOpenMatter.set(true);
        }
    }

    async updateValidators() {
        const vatControl = this.paymentRequestForm.get('vatAmount');
        if (this.paymentType) {
            vatControl?.setValidators([Validators.required, Validators.pattern(/^[0-9.,]*$/)]);
        } else {
            vatControl?.clearValidators();
        }
        vatControl?.updateValueAndValidity();

        const internationalFields = [
            'finalBeneficiaryAccNo',
            'finalBeneficiarySwift',
            'finalReference',
            'intermAccountNo',
            'intermSwift',
            'intermBankName',
            'interReference',
        ];

        if (this.isInternational) {
            this.currencyDropdown = await this.requestService.getAllCurrencies();

            this.paymentRequestForm.get('currency')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalBeneficiaryAccNo')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalBeneficiarySwift')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalReference')?.setValidators([Validators.required]);

            this.paymentRequestForm.get('intermAccountNo')?.clearValidators();
            this.paymentRequestForm.get('intermSwift')?.clearValidators();
            this.paymentRequestForm.get('intermBankName')?.clearValidators();
            this.paymentRequestForm.get('interReference')?.clearValidators();
        } else {
            this.paymentRequestForm.get('sortCode')?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
            this.paymentRequestForm.get('accountNo')?.setValidators([Validators.required]);

            internationalFields.forEach(field => {
                const control = this.paymentRequestForm.get(field);
                control?.clearValidators();
                control?.setValue(null);
                control?.updateValueAndValidity();
            });
        }

        const isChaps = this.paymentRequestForm.get('chapsRequired')?.value === 'CHAPS';
        const addressCtrl = this.paymentRequestForm.get('address');
        addressCtrl?.clearValidators();
        if (this.isInternational) {
            addressCtrl?.setValidators([Validators.required]);
        } else if (!isChaps) {
            addressCtrl?.setValue('', { emitEvent: false });
        }
        addressCtrl?.updateValueAndValidity();

        internationalFields.forEach(field => {
            this.paymentRequestForm.get(field)?.updateValueAndValidity();
        });
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    async getPurchaseCodes() {
        try {
            this.purposeCodeDropdown = await this.requestService.getPurchaseCodeLookup();
        } catch (error) {
            console.error("Error fetching purchase codes:", error);
        }
    }

    filterBankAccounts() {
        this.filteredBankAccounts = this.bankAccounts;
    }

    formatNumber() {
        if (this.paymentRequestForm.get('amount')?.value == 0) {
            this.displayValue = ""
            this.paymentRequestForm.get('amount')?.setValue(this.displayValue);
        } else {
            this.displayValue = this.commonService.formatNumberWithoutRounding(this.paymentRequestForm.get('amount')?.value)
            this.paymentRequestForm.get('amount')?.setValue(this.displayValue);
        }
    }

    formatVatNumber() {
        if (this.paymentRequestForm.get('vatAmount')?.value == 0) {
            this.vatDisplayValue = ""
            this.paymentRequestForm.get('vatAmount')?.setValue(this.vatDisplayValue);
        } else {
            this.vatDisplayValue = this.commonService.formatNumberWithoutRounding(this.paymentRequestForm.get('vatAmount')?.value)
            this.paymentRequestForm.get('vatAmount')?.setValue(this.vatDisplayValue);
        }
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/; // Only allow numbers, dot, and comma
        const key = event.key;

        // Allow control keys like Backspace, Arrow keys, Delete, Tab, etc.
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) {
            return;
        }

        if (!allowedKeys.test(key)) {
            event.preventDefault();
        }
    }


    togglePurposeCodeDropdown() {

        const control = this.paymentRequestForm.get('purposeCode');
        if (this.showPurposeCodeDropdown) {
            control?.enable();
            control?.setValidators(Validators.required);
        } else {
            control?.setValue(null);
            control?.disable();
            control?.clearValidators();
        }
        control?.updateValueAndValidity();
    }

    onPropertyPurchaseChange(event: MatCheckboxChange): void {
        this.showPurposeCodeDropdown = event.checked;
        if (!this.showPurposeCodeDropdown) {
            this.paymentRequestForm.get('purposeCode')?.setValue(null);
        }
        this.togglePurposeCodeDropdown();
    }

    onPaymentMethodChange(value: string): void {
        this.showPropertyPurchaseCheckbox = value === 'CHAPS';
        if (!this.showPropertyPurchaseCheckbox) {
            this.paymentRequestForm.get('isPropertyPurchase')?.setValue(false);
            this.paymentRequestForm.get('purposeCode')?.setValue(null);
            this.showPropertyPurchaseCheckbox = false
            this.showPurposeCodeDropdown = false
        }

        const addressCtrl = this.paymentRequestForm.get('address');
        addressCtrl?.clearValidators();
        if (this.isInternational) {
            addressCtrl?.setValidators([Validators.required]);
        } else if (value !== 'CHAPS') {
            addressCtrl?.setValue('', { emitEvent: false });
        }
        addressCtrl?.updateValueAndValidity();
    }

    private setupAutocomplete() {
        this.paymentRequestForm.controls['matter'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query) => {
                let id: string = '';
                let name: string = '';

                if (!isNaN(Number(query))) {
                    id = query;
                    name = '';
                }
                else {
                    id = '';
                    name = query;
                }
                if (id == null) {
                    id = ''
                }
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(id, name);
            })
        ).subscribe({
            next: (data) => {
                this.noData = false;
                if (data == null) {
                    this.noData = true;
                }
                this.matterDropdownData = data;
                this.isLoading = false;
            },
            error: (error) => {
                this.noData = true;
                if (error.status == 401) {
                    this.commonService.apiSecurityError();
                }
            },
            complete: () => {
                this.isLoading = false;
            }
        })
    }

    async clearSearchFilter() {
        this.paymentRequestForm.controls.matter.reset();
        this.paymentRequestForm.controls.matterNumber.reset();
        this.matterDropdownData = [];
    }

    displayMatter(matter: Matter) {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    async lookupSelecttionChangd(event: MatOptionSelectionChange<string>) {
        if (!event.isUserInput) return;

        const res = event.source.viewValue;
        if (!res || !res.includes(' - ')) return;

        this.matterNo = res.split(' - ')[0];
        this.matterDesc = res.split(' - ')[1];

        if (this.previousMatterNumber === this.matterNo) return;
        this.previousMatterNumber = this.matterNo;

        this.paymentRequestForm.patchValue({
            matter: `${this.matterNo} - ${this.matterDesc}`,
            matterNumber: this.matterNo
        });

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.paymentRequestForm.get('clientName')?.setValue('');

        await this.getClientDetails(Number(this.matterNo));
    }

    /**
     * Loads the matter's clients into the Client Name field and as the options the payee is ticked from.
     * @param resetPayee True when the matter has changed or "monies returned" was just set to Yes: the Client Name is
     * refreshed and a payee that follows the client is chosen again. False when the request is first shown, so the
     * Client Name and payee saved with the request are kept.
     */
    async getClientDetails(matterNumber: number, resetPayee = true) {
        try {
            this.clientOptions = await this.matterService.getMatterClientNames(matterNumber);
        } catch {
            this.clientOptions = [];
        }
        if (resetPayee) {
            this.paymentRequestForm.get('clientName')?.setValue(
                this.clientOptions.length ? this.clientOptions.join(', ') : 'No client found');
        }

        if (resetPayee && this.paymentRequestForm.get('isMoniesReturned')?.value === true) {
            // With several clients the user ticks which of them is the payee, so it starts empty.
            this.paymentRequestForm.get('name')?.setValue(this.clientOptions.length > 1 ? '' : (this.clientOptions[0] ?? ''));
        }
        this.syncPayeeClients();
    }

    /** True when monies go to the client, the matter has several clients and the payee text can be shown as ticks. */
    get showPayeeSelect(): boolean {
        return this.clientOptions.length > 1
            && this.paymentRequestForm.get('isMoniesReturned')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.paymentRequestForm.get('name')?.value ?? '';
        const ticked = this.payeeClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    /** Sets the text to the ticked clients, in the order they are listed. */
    onPayeeClientsChange(): void {
        const ticked = this.payeeClients.value;
        this.paymentRequestForm.get('name')?.setValue(this.clientOptions.filter(name => ticked.includes(name)).join(', '));
    }

    onPayeeSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.paymentRequestForm.get('name')?.markAsTouched();
    }

    /** Ticks the listed clients that appear in the current text, and locks the dropdown when the field is locked. */
    private syncPayeeClients(): void {
        const payeeControl = this.paymentRequestForm.get('name');
        const padded = `, ${payeeControl?.value ?? ''}, `;
        this.payeeClients.setValue(this.clientOptions.filter(name => padded.includes(`, ${name}, `)));
        if (payeeControl?.disabled) {
            this.payeeClients.disable();
        } else {
            this.payeeClients.enable();
        }
    }


    reasonVisible: boolean = false;

    private setupFormSubscriptions() {
        this.paymentRequestForm.get('matter')?.valueChanges.subscribe((matter) => {
            if (!matter) {
                this.paymentRequestForm.get('isMoniesReturned')?.setValue(false);
                this.paymentRequestForm.get('isMoniesReturned')?.disable();
                this.paymentRequestForm.get('name')?.disable();
            } else {
                this.paymentRequestForm.get('isMoniesReturned')?.enable();
                this.paymentRequestForm.get('name')?.enable();
            }
        });

        this.paymentRequestForm.get('isMoniesReturned')?.valueChanges.subscribe(async (isMoniesReturned) => {
            if (isMoniesReturned === true) {
                this.reasonVisible = true;
                this.paymentRequestForm.get('reason')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('reason')?.updateValueAndValidity();

                this.isLoading = true;
                try {
                    await this.getClientDetails(Number(this.matterNo));
                } finally {
                    this.isLoading = false;
                }
            } else if (isMoniesReturned === false) {
                this.reasonVisible = true;
                this.paymentRequestForm.get('reason')?.setValue(this.payment.reason);
                this.paymentRequestForm.get('reason')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('reason')?.updateValueAndValidity();

                this.paymentRequestForm.get('returnMoniesExplanationIfNo')?.setValue(this.payment.returnMoniesExplanationIfNo);
                this.paymentRequestForm.get('returnMoniesExplanationIfNo')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('returnMoniesExplanationIfNo')?.updateValueAndValidity();
                this.paymentRequestForm.get('name')?.setValue(null);
                this.payeeClients.setValue([]);
            }
        });

        this.paymentRequestForm.get('isAccountDetailsVerifies')?.valueChanges.subscribe((value) => {
            if (value === 'Yes') {
                this.paymentRequestForm.get('verificationDate')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('spokeTo')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('verificationNoReason')?.clearValidators();
                this.paymentRequestForm.patchValue({ verificationNoReason: '' });
            } else if (value === 'No') {
                this.paymentRequestForm.get('verificationNoReason')?.setValidators([Validators.required]);
                this.paymentRequestForm.get('verificationDate')?.clearValidators();
                this.paymentRequestForm.get('spokeTo')?.clearValidators();
                this.paymentRequestForm.patchValue({
                    verificationDate: null,
                    spokeTo: ''
                });
            }

            this.paymentRequestForm.get('verificationDate')?.updateValueAndValidity();
            this.paymentRequestForm.get('spokeTo')?.updateValueAndValidity();
            this.paymentRequestForm.get('verificationNoReason')?.updateValueAndValidity();
        });

        this.paymentRequestForm.get('matterPartner')?.valueChanges.subscribe((val) => {
            if (val) {
                this.paymentRequestForm.get('nonPartnerReason')?.disable();
                this.paymentRequestForm.patchValue({ nonPartnerReason: null });
            } else {
                this.paymentRequestForm.get('nonPartnerReason')?.enable();
                this.paymentRequestForm.get('nonPartnerReason')?.setValue(this.payment.nonPartnerReason);
            }
        });
    }

    allowOnlyDigits(event: KeyboardEvent): void {
        if (!/^[0-9]$/.test(event.key)) {
            event.preventDefault();
        }
    }

    sanitizeSortCode(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = input.value.replace(/\D/g, '').slice(0, 6);
        if (digits !== input.value) {
            input.value = digits;
            this.paymentRequestForm.get('sortCode')?.setValue(digits);
        }
    }

    closeDialog() {
        this.dialogRef.close(true);
    }

    private toggleFormFieldsByStage() {

        const isParalegalStage = this.payment.currentStage?.stageOrder === 2;
        const isPartnerStage = (this.payment.currentStage?.stageOrder === 3 && this.payment.isCreatorPartnerApprove) ?? false;

        this.isRejectDisabled = this.payment.currentStage?.stageOrder === 1 || (this.payment.currentStage?.stageOrder ?? 0) >= 5;
        this.isWithdrawDisabled = this.payment.currentStage?.stageOrder === 1 || this.payment.currentStage?.stageOrder === 7;

        const isEditable = isParalegalStage || isPartnerStage;
        this.isDisabled = !(isParalegalStage || isPartnerStage);

        Object.keys(this.paymentRequestForm.controls).forEach(controlName => {
            const control = this.paymentRequestForm.get(controlName);
            if (isEditable) {
                control?.enable();
            } else {
                control?.disable();
            }
        });
        this.showReturnReason = isEditable;

        if (!isEditable) {
            this.showPurposeCodeDropdown = false;
            this.showPropertyPurchaseCheckbox = false;
        }

        this.paymentRequestForm.get('dateRequested')?.disable();
        this.paymentRequestForm.get('requestedTime')?.disable();
    }

    onFilesSelected(input: any) {
        if (!input.files) return;

        this.fileNames = [];
        this.displayFile = [];

        const filesArray = Array.from(input.files);
        let filesProcessed = 0;

        filesArray.forEach((file: any) => {
            const fileName = file.name;
            this.displayFile.push(fileName);

            const reader = new FileReader();
            reader.onload = (e: any) => {
                const bytes = e.target.result.split('base64,')[1];
                this.fileNames.push({ fileName: fileName, attachments: bytes });

                filesProcessed++;
                if (filesProcessed === filesArray.length) {
                    this.paymentRequestForm.get('uploadedFiles')?.setValue(this.fileNames);
                    this.paymentRequestForm.get('uploadedFiles')?.updateValueAndValidity();
                }
            };
            reader.readAsDataURL(file);
        });
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    clearUpload(fileInput: any) {
        this.displayFile = [];
        this.fileNames = [];
        this.paymentRequestForm.get('uploadedFiles')?.setValue([]);
        fileInput.value = '';
    }

    uploadDoc() {
        if (this.paymentRequestForm.get('uploadedFiles')?.value.length > 0) {
            let docData: RequestDocAttachmentResponse = {
                requestId: this.payment.id,
                requestType: RequestType.Payment,
                attachments: this.paymentRequestForm.get('uploadedFiles')?.value
            }

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            })
        }
        this.dialogRef.close(true);
    }

    delete() {
        var configOk = {
            title: 'Are you sure?',
            message: 'The data associated with the request will be deleted.',
            dialogType: 'warning',
            buttonType: 'ConfirmCancel',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: configOk,
            width: '30%',
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.requestService.deletePaymentRequest(this.payment.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete payment request. Please try again.");
                    });
            }
        })
    }

    async withdrawPayment() {
        const audits = await this.auditService.getUserAuditByRequest(this.payment.id, RequestTypeOrder[RequestType.Payment]);
        let latestAudit = null;

        if (this.payment?.currentStage?.stageOrder == 5) {
            latestAudit = audits?.length ? audits[audits.length - 1] : null;
        } else {
            latestAudit = audits?.length ? audits[audits.length - 2] : null;
        }
        const rpaStatus = latestAudit?.wasRpaSuccessful ?? null;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Withdraw Payment Request?',
                message: 'Are you sure you want to withdraw this payment request?',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.payment?.matterNo,
                currentStageId: this.payment?.currentStage?.id,
                rpaStatus: rpaStatus,
                skipAccountsValidation: true,
                selectedBankAccountId: this.selectedBankAccountId,
            }
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.userRequestService.withdrawPaymentRequest(this.payment.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to withdraw payment request. Please try again.");
                    });
            }
        })
    }
    async approveRequest() {
        if (this.paymentRequestForm.invalid) {
            this.paymentRequestForm.markAllAsTouched();
            return;
        }
        const selectedPurpose = this.purposeCodeDropdown.find(
            p => p.id === this.paymentRequestForm.value.purposeCode
        );

        const isPartnerStage = (this.payment.currentStage?.stageOrder === 3 && this.payment.isCreatorPartnerApprove) ?? false;
        const selectedAccount = this.resolveBankAccount(this.selectedBankAccountId);
        const approvalDetails: ApprovalDetails = {
            clientBankAccountId: this.selectedBankAccountId || this.payment.clientBankAccountId,
            clientBankAccountName: selectedAccount
                ? `${selectedAccount.accountId} - ${selectedAccount.description}`
                : this.payment.clientBankAccountName,
            requestDate: this.paymentRequestForm.value.dateRequested,
            reasonForPayment: this.paymentRequestForm.value.reason,
            returnMoniesExplanationIfNo: this.paymentRequestForm.value.returnMoniesExplanationIfNo,
            bankName: this.paymentRequestForm.value.bankName,
            sortCode: this.paymentRequestForm.value.sortCode,
            accountNumber: this.paymentRequestForm.value.accountNo,
            chapsRequired: this.paymentRequestForm.value.chapsRequired === 'CHAPS',
            isPropertyPurchase: this.paymentRequestForm.value.isPropertyPurchase,
            accountTelephoneVerification: this.paymentRequestForm.value.isAccountDetailsVerifies === 'Yes',
            telephoneVerificationDate: this.paymentRequestForm.value.verificationDate,
            verificationSpokeTo: this.paymentRequestForm.value.spokeTo,
            verificationNoReason: this.paymentRequestForm.value.verificationNoReason,
            otherInfo: this.paymentRequestForm.value.otherInfo,
            address: this.paymentRequestForm.value.address,
            amount: this.commonService.parseFormattedNumber(this.paymentRequestForm.value.amount),
            vatAmount: this.commonService.parseFormattedNumber(this.paymentRequestForm.value.vatAmount),
            reference: this.paymentRequestForm.value.reference,
            finalBeneficiaryAccNo: this.paymentRequestForm.value.finalBeneficiaryAccNo,
            finalBeneficiarySwift: this.paymentRequestForm.value.finalBeneficiarySwift,
            finalReference: this.paymentRequestForm.value.finalReference,
            intermAccountNo: this.paymentRequestForm.value.intermAccountNo,
            intermSwift: this.paymentRequestForm.value.intermSwift,
            intermBankName: this.paymentRequestForm.value.intermBankName,
            interReference: this.paymentRequestForm.value.interReference,
            isMoniesReturned: this.paymentRequestForm.value.isMoniesReturned,
            paymentMethod: this.paymentRequestForm.value.chapsRequired === 'CHAPS' ? 'CHAPS' : 'Faster Pay',
            payee: this.paymentRequestForm.value.name,
            purchaseCode: selectedPurpose ? selectedPurpose.code : null,
            matter: this.matterDesc || this.payment.matter,
            matterNumber: this.paymentRequestForm.value.matterNumber?.toString(),
            matterPartner: this.paymentRequestForm.value.matterPartner,
            nonPartnerReason: this.paymentRequestForm.value.nonPartnerReason,
        };

        if (isPartnerStage) {
            approvalDetails.fundsCheckedByPartner = true;
            approvalDetails.isPartnerCertified = true;
        }

        this.requestService.approveRequest(
            this.payment.id,
            RequestType.Payment,
            (this.payment.currentStage!.id) + 1,
            approvalDetails
        )
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.dialogRef.close(true);
                    this.uploadDoc();
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve payment request. Please try again.");
            });
    }

}
